import type { ShipmentDraft } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentDraft";
import { useMemo } from "react";
import { TrashIcon } from "@phosphor-icons/react/Trash";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { bookShipmentLoadDraftState } from "@/Slices/Helpers/bookShipmentLoadDraftState";
import ShipmentRowTemplate from "@/Shared/Shipment/CustomComponents/ShipmentRowTemplate";
import { useNavigate } from "react-router-dom";
import { ShipmentRowTimeValue } from "@/Shared/Shipment/CustomComponents/ShipmentRowTimeValue";
import moment from "moment";
import { useShipmentDraftsApi } from "@/Hooks/useShipmentDraftsApi";
import type { UUID } from "@/Shared/types";

interface ShipmentDraftRowProps {
  draft: ShipmentDraft;
  onRefresh: () => Promise<void>;
}

export function ShipmentDraftsRow(props: ShipmentDraftRowProps) {
  const { draft } = props;
  const navigate = useNavigate();

  const draftsApi = useShipmentDraftsApi();

  const { shipment } = useMemo(
    () =>
      bookShipmentLoadDraftState({
        draft: {
          ...draft,
          createdAt: draft.createdAt?.toISOString(),
          pickupLatitudeLongitude: undefined,
          deliveryLatitudeLongitude: undefined,
        },
        savedLocations: [],
      }),
    [draft.shipmentDraftId],
  );

  function onClick() {
    navigate(`/book/draft?shipmentDraftId=${draft.shipmentDraftId}`);
  }

  // The delete is gated by the Button's own confirm, so this just does the work
  // once the user has confirmed.
  async function onDelete(shipmentDraftId: UUID) {
    await draftsApi.deleteShipmentDraft({ shipmentDraftId });
    await props.onRefresh();
  }

  return (
    <div data-testid="draft-row" className="flex items-center">
      <ShipmentRowTemplate
        colorBar="#8B5CF6"
        state="Draft"
        carrierIdentifier={undefined}
        lineItems={shipment.lineItems}
        pickupLocation={shipment.pickupLocation}
        deliveryLocation={shipment.deliveryLocation}
        onClick={onClick}
        pickupReference={shipment.pickupReferenceNumber || ""}
        deliveryReference={shipment.deliveryReferenceNumber || ""}
      >
        {draft.createdAt && (
          <ShipmentRowTimeValue label="Created at">
            {moment(draft.createdAt).format("Do MMMM YYYY")}
          </ShipmentRowTimeValue>
        )}
      </ShipmentRowTemplate>
      {draft.shipmentDraftId !== undefined && (
        <Button
          size="link"
          tone="danger"
          icon={TrashIcon}
          data-testid="delete-draft-button"
          confirm={{
            title: "Confirm delete",
            message: "Are you sure you want to delete this shipment draft?",
            confirmLabel: "Delete",
          }}
          onClick={() => onDelete(draft.shipmentDraftId!)}
        />
      )}
    </div>
  );
}
